import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import styles from './TextArea.module.css';

export interface TextAreaProps {
  /** Returns entered values as trimmed string */
  onChange: (value: string) => void;
  /** The label for the text area */
  label?: string;
  /** Optional sub label for the text area */
  subLabel?: string;
  /** The max amount of allowed charecters in the text area */
  maxCharacters?: number;
  /** The max number of visible lines in the text area */
  heightAsTextField?: boolean;
  /** The default value for the text area */
  defaultValue?: string;
  /** `true` to show an asterisk to indicate an item is required; otherwise, `false`. */
  required?: boolean;
  /** `true` to color border red to show not valid; otherwise, `false`. */
  notValid?: boolean;
}

export function TextArea({
  onChange,
  label,
  subLabel,
  maxCharacters = 0,
  heightAsTextField = false,
  defaultValue = '',
  required = false,
  notValid = false,
}: TextAreaProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [characterCount, setCharacterCount] = useState(defaultValue.length);

  const handleInput = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;

      setCharacterCount(textAreaRef.current.value.length);
      onChange(textAreaRef.current.value.trim());
    }
  };

  const autoResize = () => {
    const el = textAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (!heightAsTextField) {
      autoResize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles['text-area__wrapper']}>
      {(label || maxCharacters > 0) && (
        <div className={styles['text-area__header']}>
          <span className={styles['text-area__label-wrapper']}>
            {label && (
              <span className={clsx(styles['text-area__label'], { [styles['required']]: required })}>{label}</span>
            )}
            {subLabel && <span className={styles['text-area__sub-label']}>{subLabel}</span>}
          </span>
          {maxCharacters > 0 && (
            <div className={styles['text-area__word-count']}>
              <span className={clsx({ [styles['text-area__word-count--alert']]: characterCount === maxCharacters })}>
                {characterCount}
              </span>
              &nbsp;/&nbsp;{maxCharacters}
            </div>
          )}
        </div>
      )}

      <textarea
        ref={textAreaRef}
        className={clsx(styles['text-area'], {
          [styles['not-valid']]: notValid,
          [styles['text-area--oneLine']]: heightAsTextField,
        })}
        maxLength={maxCharacters > 0 ? maxCharacters : undefined}
        onInput={handleInput}
        defaultValue={defaultValue}
      />
    </div>
  );
}
